import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useRecoilValue } from "recoil";
import { Button, Theme } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { getTableData } from "../services";
import { triggerUnmappeQueryAtom } from "../atoms";
import { ActionTooltip } from "../../components/Tooltips";

const useStyles = makeStyles()((theme: Theme) => ({
  buttonFilter: {
    backgroundColor: theme.vars.palette.button.tonalBackground,
    border: `1px solid ${theme.vars.palette.filter.toggleBorder}`,
    fontSize: "0.875rem",
    height: "38px",
    padding: "4px 11px",

    "&.MuiButton-text.MuiButton-colorInherit": {
      color: theme.vars.palette.text.primary,
    },
    "&.MuiButton-colorPrimary": {
      backgroundColor: theme.vars.palette.filter.toggleSelectedBackground,
      color: theme.vars.palette.filter.toggleSelectedText,
    },
  },
}));

const useGetNbUnmapped = (api: any) => {
  const reloadUnmappedAtom = useRecoilValue(triggerUnmappeQueryAtom);

  const query = useQuery(["filter-count", api, reloadUnmappedAtom], () =>
    getTableData(api)
  );

  return query;
};

/**
 * Toggle button for a boolean filter whose count is read from `api`, which may
 * be either a list route (the count comes from the paginated envelope) or a
 * dedicated count action. `icon` and `tooltip` default to the unmapped filter
 * this was written for; other boolean filters pass their own.
 */
export const UnmappedFilter = ({
  config: { api },
  onClick,
  value,
  icon = <LinkOffIcon />,
  tooltip = "UNMAPPED_VALUES",
}: {
  config: { api: string };
  onClick: (newValue: boolean) => void;
  value?: boolean;
  icon?: React.ReactNode;
  tooltip?: string;
}) => {
  const intl = useIntl();
  const { classes } = useStyles();
  const [active, setActive] = useState<boolean>(() => !!value);
  const unmappedQuery = useGetNbUnmapped(api);

  useEffect(() => {
    setActive(!!value);
  }, [value]);

  return (
    <ActionTooltip
      title={intl.formatMessage({ id: tooltip })}
      translateTitle={false}
      rawChildren
    >
      <span>
        <Button
          className={classes.buttonFilter}
          color={active ? "primary" : "inherit"}
          disabled={unmappedQuery.isFetched && !unmappedQuery.data?.count}
          onClick={() => {
            onClick(!active);
            setActive(!active);
          }}
          startIcon={icon}
          size="small"
        >
          {unmappedQuery.data?.count}
        </Button>
      </span>
    </ActionTooltip>
  );
};
